import { Component, EventEmitter, Input, Output } from '@angular/core'
import { MenuItem, MessageService } from 'primeng/api'

import { AuthService } from '@data/services/modules/auth/auth.service'
import { ApiInvoiceService, ImportInvoiceResult, UploadInvoiceFilesResult } from '../../data/services/api/api-invoice.service'

// Modal de "Carga masiva": wizard de 2 pasos (Excel de referencias + archivos PDF/XML).
@Component({
	selector: 'app-invoices-upload-dialog',
	templateUrl: './invoices-upload-dialog.component.html',
	styleUrls: ['./invoices-upload-dialog.component.scss'],
	providers: [MessageService],
})
export class InvoicesUploadDialogComponent {
	@Input() visible = false
	@Output() visibleChange = new EventEmitter<boolean>()
	@Output() completed = new EventEmitter<void>() // tras import/carga exitosa → refrescar dashboard

	steps: MenuItem[] = [{ label: 'Referencias (Excel)' }, { label: 'Archivos (PDF / XML)' }]
	activeStep = 0

	// Paso 1 — Excel
	excelFile: File | null = null
	importing = false
	importResult: ImportInvoiceResult | null = null
	downloadingTemplate = false

	// Paso 2 — Archivos
	selectedFiles: File[] = []
	uploading = false
	uploadResult: UploadInvoiceFilesResult | null = null
	isDraggingFiles = false

	constructor(
		private readonly api: ApiInvoiceService,
		private readonly messageService: MessageService,
		private readonly authService: AuthService,
	) {}

	private get userId(): string | undefined {
		return this.authService.getUser?.user?.id
	}

	onHide(): void {
		this.visibleChange.emit(false)
		this.resetState()
	}
	close(): void {
		this.visible = false
		this.visibleChange.emit(false)
	}
	// "Finalizar": cierra el wizard tras una carga exitosa y refresca el dashboard.
	finish(): void {
		this.completed.emit()
		this.close()
	}
	// Limpia el wizard al cerrarse para que la próxima apertura arranque desde el paso 1.
	private resetState(): void {
		this.activeStep = 0
		this.excelFile = null
		this.importing = false
		this.importResult = null
		this.selectedFiles = []
		this.uploading = false
		this.uploadResult = null
		this.isDraggingFiles = false
	}

	downloadTemplate(): void {
		this.downloadingTemplate = true
		this.api.downloadTemplate().subscribe({
			next: (res) => {
				this.downloadingTemplate = false
				const blob = res.body
				if (!blob) {
					this.toastErr('Descarga vacía', 'El servidor no devolvió contenido.')
					return
				}
				this.triggerDownload(blob, 'plantilla-facturas.xlsx')
			},
			error: () => {
				this.downloadingTemplate = false
				this.toastErr('Error', 'No se pudo descargar la plantilla.')
			},
		})
	}

	onExcelSelected(event: Event): void {
		const input = event.target as HTMLInputElement
		this.excelFile = input.files && input.files.length ? input.files[0] : null
	}

	importExcel(): void {
		if (!this.excelFile) {
			this.toastWarn('Sin archivo', 'Selecciona el Excel de referencias.')
			return
		}
		this.importing = true
		this.importResult = null
		this.api.importExcel(this.excelFile, this.userId).subscribe({
			next: (r) => {
				this.importing = false
				if (r.error || !r.data) {
					this.toastErr('Error al importar', r.msg)
					return
				}
				this.importResult = r.data
				this.toastOk('Excel procesado', `${r.data.created} creadas, ${r.data.updated} actualizadas, ${r.data.errors} con error.`)
				this.completed.emit()
			},
			error: () => {
				this.importing = false
				this.toastErr('Error', 'Falló la importación del Excel.')
			},
		})
	}

	onFilesSelected(event: Event): void {
		const input = event.target as HTMLInputElement
		this.selectedFiles = input.files ? Array.from(input.files) : []
	}

	onFilesDragOver(event: DragEvent): void {
		event.preventDefault()
		this.isDraggingFiles = true
	}
	onFilesDragLeave(event: DragEvent): void {
		event.preventDefault()
		this.isDraggingFiles = false
	}
	onFilesDrop(event: DragEvent): void {
		event.preventDefault()
		this.isDraggingFiles = false
		const dropped = event.dataTransfer?.files
		if (dropped && dropped.length) {
			this.selectedFiles = Array.from(dropped).filter((f) => /\.(pdf|xml)$/i.test(f.name))
		}
	}

	uploadFiles(): void {
		if (!this.selectedFiles.length) {
			this.toastWarn('Sin archivos', 'Selecciona los PDF/XML a cargar.')
			return
		}
		this.uploading = true
		this.uploadResult = null
		this.api.uploadFiles(this.selectedFiles, this.userId).subscribe({
			next: (r) => {
				this.uploading = false
				if (r.error || !r.data) {
					this.toastErr('Error al subir', r.msg)
					return
				}
				this.uploadResult = r.data
				this.toastOk('Archivos procesados', `${r.data.uploaded} subidos · ${r.data.skippedUnmatched.length} sin coincidencia · ${r.data.errors.length} con error.`)
				this.completed.emit()
			},
			error: () => {
				this.uploading = false
				this.toastErr('Error', 'Falló la carga de archivos.')
			},
		})
	}

	statusSeverity(status: string): string {
		switch (status) {
			case 'CREATED':
			case 'UPLOADED':
				return 'success'
			case 'UPDATED':
				return 'info'
			case 'SKIPPED':
				return 'warning'
			default:
				return 'danger'
		}
	}

	private triggerDownload(blob: Blob, filename: string): void {
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = filename
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
		setTimeout(() => URL.revokeObjectURL(url), 1000)
	}

	private toastOk(summary: string, detail: string): void {
		this.messageService.add({ severity: 'success', summary, detail, life: 6000 })
	}
	private toastErr(summary: string, detail: string): void {
		this.messageService.add({ severity: 'error', summary, detail, life: 8000 })
	}
	private toastWarn(summary: string, detail: string): void {
		this.messageService.add({ severity: 'warn', summary, detail, life: 5000 })
	}
}
